import {
  Box,
  Card,
  Center,
  Flex,
  Text,
  Stack,
  Image,
  useTheme,
  useMediaQuery,
  HStack,
} from "@chakra-ui/react";
import { EntryBody } from "../../../feature/EntryBody";
import { useGetCardEntriesByCardIdQuery } from "../../../graphql/generated/schema";
import { useCard, useRoutes, useSingleCardEntry } from "../../../hooks";
import { AddEntryOrUpgradeCardButton } from "./CardHeader/AddEntryOrUpgradeCardButton";
import { SingleEntryCardSettings } from "./SingleEntryCardSettings";
import { Loader } from "../../../components";
import { useScreen } from "../../../hooks";
import { useEditedCard } from "../../../hooks/useEditedCard";
import useEditCardSettingDrawer from "../../../hooks/useEditCardSettingDrawer";

function ScrollableFadeContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box position="relative" height="100%">
      <Box
        position="absolute"
        top="0"
        bottom="0"
        left="0"
        right="0"
        overflowY="auto"
        pr="4px" // To prevent scrollbar from overlapping content
        pb="50px" // Same height as gradient
      >
        {/* Your overflowing content goes here */}
        {children}
      </Box>
      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        height="50px"
        backgroundImage="linear-gradient(to top, white, transparent)"
        // backgroundImage="linear-gradient(to top, black, transparent)"
      />
    </Box>
  );
}

export function SingleEntryBody() {
  const { card: serverCard, isUserCardOwner } = useCard();
  const { editedCard } = useEditedCard();
  const card = editedCard ?? serverCard;

  const { handleOpenCardSettingsDrawer } = useEditCardSettingDrawer();
  const {
    imageHeight,
    imageWidth,
    entryImg,
    isLoading: isLoadingCardEntry,
  } = useSingleCardEntry();
  const { isDesktop } = useScreen();
  const isOrientationLandscape = card?.isLandscape;
  const showLandscape = isDesktop ? isOrientationLandscape : false;
  const flexItemStyles = {
    width: showLandscape ? "50%" : "100%",
  };
  const containerHalfWidth = 512;
  let aspectRatio;
  let containerHeight;

  if (imageWidth && imageHeight) {
    aspectRatio = Number(imageWidth) / Number(imageHeight); // replace with your aspect ratio
    containerHeight = containerHalfWidth / aspectRatio;
  }

  if (isLoadingCardEntry) {
    return <Loader />;
  }

  const editCardHeaderProps = isUserCardOwner
    ? {
        _hover: {
          borderColor: "blue.200",
          cursor: "pointer",
        },
        onClick: handleOpenCardSettingsDrawer({
          index: 1,
        }),
      }
    : {};

  // const lorem =
  //   "Lorem ipsum dolor sit amet consectetur adipisicing elit. Minus rerum autem nemo nostrum fugiat explicabo veniam. Iure amet accusantium omnis cum, odit officia reiciendis laboriosam facilis enim impedit sequi beatae distinctio numquam debitis voluptatem perspiciatis eos tempora. Molestiae placeat quis repellendus beatae quaerat vitae nemo, adipisci culpa dicta laborum, esse eligendi maiores? Porro delectus, ea itaque reprehenderit distinctio explicabo aliquid maiores placeat officiis cum nulla, deleniti aspernatur voluptate? Velit error eum, placeat odit vitae tempora reprehenderit eaque eligendi blanditiis, eos doloribus est animi nesciunt, vel neque. Amet quae nihil quidem rerum culpa nam numquam eius quasi, autem doloremque fuga ut suscipit illum et ipsa quibusdam maiores dolore omnis similique? Odio recusandae perferendis alias quia nesciunt minus illo nulla aliquam nemo aut natus, consectetur excepturi veniam atque, ut quasi deleniti perspiciatis nisi omnis reiciendis eaque quidem. Cumque atque tempore consectetur non voluptatem! At consequuntur corporis soluta, officiis aperiam nesciunt beatae sit ab ipsam eum ut cupiditate explicabo consequatur excepturi cumque blanditiis eveniet veritatis eaque mollitia quo dolorum? Natus amet distinctio nihil rerum ex perspiciatis et consequuntur obcaecati consequatur ullam! Ex dicta sit hic, ut ipsa modi vero vitae provident! Vero quasi adipisci nihil suscipit odit. Unde, error. Soluta earum autem obcaecati doloremque quo omnis reprehenderit placeat error asperiores cumque veniam voluptatibus beatae, debitis, magni, iusto necessitatibus porro dignissimos non libero sapiente provident repellat quis alias odit? Libero, quibusdam. Unde illo exercitationem inventore. Consectetur nostrum illum, tempore alias illo quisquam nesciunt labore ipsum repellat ex quod aliquam, necessitatibus fuga quo officiis molestias in possimus provident obcaecati? Eveniet itaque saepe, delectus pariatur numquam qui quo, reiciendis adipisci nam voluptatum esse quos id ipsum totam tempora quae voluptate, molestiae sit. Ut fugiat vel optio esse aspernatur rem corporis odit vero adipisci nemo. Atque, voluptas temporibus. Maxime inventore obcaecati laborum vel? Quam est, impedit quisquam ullam voluptas velit incidunt excepturi magni harum repellendus. Nulla deleniti laudantium obcaecati quod eum totam explicabo ab, corrupti, provident molestias dolorem sequi repellat deserunt. Dicta accusantium facere hic ipsa tempore minima maiores aliquam impedit voluptate dolor, reprehenderit ratione, sit culpa soluta eius totam est nostrum eos! Accusantium, ea? Fuga tempora praesentium, eveniet illo reiciendis, maiores quasi dignissimos qui quaerat alias accusamus repellat sit vitae quis. Deserunt eveniet cum totam accusamus deleniti ratione culpa minima labore quam saepe officia quasi, exercitationem, aperiam excepturi laborum soluta? A doloribus rerum repudiandae! Eos animi iure reprehenderit ullam ad fugiat dolorem doloremque magnam veritatis natus ex, amet asperiores harum aliquam assumenda autem iste! Sed fugit tenetur unde error facere porro eos dolor facilis, temporibus voluptatibus autem, optio illo vero natus necessitatibus cupiditate sapiente deserunt laudantium inventore harum maxime? Corporis hic quis obcaecati, eveniet perferendis iure accusantium, esse eos incidunt a reiciendis nulla ipsam accusamus quaerat. Consequatur facere pariatur amet ipsam doloribus praesentium repellendus esse corporis nostrum, officia cum soluta harum dolor accusamus voluptates architecto doloremque cupiditate dignissimos quo atque recusandae nobis eius. Nostrum ut aperiam minima dolores alias culpa incidunt, ullam mollitia sequi quod laboriosam enim ducimus voluptatibus quidem quae voluptate quaerat harum ea. Iure.";

  const renderTitleAndEntry = (
    <Stack spacing="lg" w="full" p="lg">
      <Box
        border="2px solid transparent"
        px="md"
        py="sm"
        rounded="md"
        {...editCardHeaderProps}
      >
        <Text
          textAlign={"center"}
          fontSize="3xl"
          fontFamily={card?.titleFontFamily}
        >
          {card?.title}
        </Text>
      </Box>
      <CardEntry />
    </Stack>
  );

  return containerHeight ? (
    <Box mx="sm">
      <Center mt="lg">
        <SingleEntryCardSettings />
      </Center>
      <Center my="lg">
        <Box
          display="flex"
          overflow="hidden"
          bg="white"
          rounded="xl"
          // border={"1px solid"}
          // borderColor="gray.50"
          // shadow={"xl"}
          // shadow={"base"}
          shadow={"dark-lg"}
          {...{
            flexDirection: showLandscape ? "row" : "column",
            height: showLandscape ? containerHeight : "100%",
            width: showLandscape ? "container.lg" : "container.sm",
          }}
        >
          <Box {...flexItemStyles}>
            <Image
              objectFit="cover"
              src={entryImg}
              alt="entry"
              height={"100%"}
              width="full"
            />
          </Box>
          <Box
            {...flexItemStyles}
            p={4}
            overflowY="auto"
            height="100%"
            position="relative"
          >
            {renderTitleAndEntry}
            {/* {showLandscape ? (
              <ScrollableFadeContainer>{renderEntry}</ScrollableFadeContainer>
            ) : (
              renderEntry
            )} */}
          </Box>
        </Box>
      </Center>
    </Box>
  ) : (
    <Box>
      <Center mt="lg">
        <SingleEntryCardSettings />
      </Center>
      <Center my="lg">
        <Flex mb="6" w="container.sm">
          <Card
            // shadow={"base"}
            shadow={"dark-lg"}
            rounded="xl"
            direction={{ base: "column", sm: "row" }}
            overflow="hidden"
            flex={1}
          >
            {renderTitleAndEntry}
          </Card>
        </Flex>
      </Center>
    </Box>
  );
}

function CardEntry() {
  const { cardId, isUserCardOwner } = useCard();
  const {
    data,
    error,
    loading: isLoading,
  } = useGetCardEntriesByCardIdQuery({
    variables: {
      input: {
        limit: 1, // TODO: Handle pagination wierdness. If you use limit of 2, and delete the top 2, pagination is returns nothing
        cardID: cardId ?? "",
      },
    },
  });
  const isError = !!error;
  const cardEntry = data?.getCardEntriesByCardId.items?.[0];

  return (
    <Box my="lg" mx="md">
      {isLoading ? (
        <Text>Loading...</Text>
      ) : isError ? (
        <Text>Error loading card entry.</Text>
      ) : cardEntry ? (
        <Box>
          {/* TODO. Eventually edit on this page directly. */}
          {/* <Textarea
            rows={6}
            rounded="xl"
            value={cardEntry?.entryMessage}
            //   color={textColor}
            //   fontFamily={selectedFont}
            onChange={(e) => {
              // handle input change here
            }}
          /> */}

          <EntryBody entry={cardEntry} />
        </Box>
      ) : (
        isUserCardOwner && <CardEntryEmptyState />
      )}
    </Box>
  );
}

function CardEntryEmptyState() {
  const { isRecipientMode } = useRoutes();

  return (
    <Flex
      border="2px dashed"
      borderColor="gray.300"
      rounded="xl"
      minH="250px"
      //   m="lg"
    >
      <Center w="full">
        {!isRecipientMode && <AddEntryOrUpgradeCardButton />}
      </Center>
    </Flex>
  );
}
